import React, { useState } from 'react';
import { Box, Paper, Typography, Grid, FormControl, InputLabel, Select, MenuItem, Card, CardContent, Tabs, Tab } from '@mui/material';
import { Bar, Pie, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';

import { usageByDepartment, usageByService, dailyUsageTrend, topUsers } from '../data/mockData';

// Register chart components
ChartJS.register(
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend, 
  ArcElement,
  PointElement,
  LineElement
);

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('7days');
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Handle time range change
  const handleTimeRangeChange = (e) => {
    setTimeRange(e.target.value);
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };
  
  // Chart data for usage by department
  const departmentChartData = {
    labels: usageByDepartment.map(item => item.department),
    datasets: [
      {
        label: 'AI Usage by Department',
        data: usageByDepartment.map(item => item.count),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Chart data for usage by service
  const serviceChartData = {
    labels: usageByService.map(item => item.service),
    datasets: [
      {
        label: 'AI Usage by Service',
        data: usageByService.map(item => item.count),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(201, 203, 207, 0.6)',
          'rgba(255, 99, 255, 0.6)',
          'rgba(54, 162, 64, 0.6)',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Chart data for daily usage trend
  const trendChartData = {
    labels: dailyUsageTrend.map(item => item.date),
    datasets: [
      {
        label: 'Daily AI Usage Trend',
        data: dailyUsageTrend.map(item => item.count),
        fill: false,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        tension: 0.1
      },
    ],
  };
  
  // Chart data for user growth
  const userGrowthData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [
      {
        label: 'Number of Users',
        data: [2, 3, 4, 5],
        fill: false,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        tension: 0.1
      },
    ],
  };
  
  // Chart data for data volume
  const dataVolumeData = {
    labels: dailyUsageTrend.map(item => item.date),
    datasets: [
      {
        label: 'Estimated Data Volume (KB)',
        data: dailyUsageTrend.map(item => item.count * 25), // Mocked data volume
        fill: false,
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        tension: 0.1
      },
    ],
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Analytics
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item>
            <Tabs value={selectedTab} onChange={handleTabChange} aria-label="analytics tabs">
              <Tab label="Usage Analytics" />
              <Tab label="User Analytics" />
              <Tab label="Data Analytics" />
            </Tabs>
          </Grid>
          
          <Grid item>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="time-range-label">Time Range</InputLabel>
              <Select
                labelId="time-range-label"
                id="time-range"
                value={timeRange}
                onChange={handleTimeRangeChange}
                label="Time Range"
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="7days">Last 7 Days</MenuItem>
                <MenuItem value="30days">Last 30 Days</MenuItem>
                <MenuItem value="90days">Last 90 Days</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Usage Analytics Tab */}
      {selectedTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
              <Typography variant="h6" gutterBottom>
                AI Usage by Department
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar 
                  data={departmentChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
              <Typography variant="h6" gutterBottom>
                AI Usage by Service
              </Typography>
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                <Pie 
                  data={serviceChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                  }}
                />
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
              <Typography variant="h6" gutterBottom>
                Daily AI Usage Trend
              </Typography>
              <Box sx={{ height: 300 }}>
                <Line 
                  data={trendChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Peak Usage Day
                </Typography>
                <Typography variant="h3" color="primary">
                  {dailyUsageTrend.reduce((max, day) => day.count > max.count ? day : max, { count: 0 }).date}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  with {dailyUsageTrend.reduce((max, day) => day.count > max.count ? day : max, { count: 0 }).count} queries
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Most Used AI Service
                </Typography>
                <Typography variant="h3" color="primary">
                  {usageByService.reduce((max, service) => service.count > max.count ? service : max, { count: 0 }).service.split(' ')[0]}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  with {usageByService.reduce((max, service) => service.count > max.count ? service : max, { count: 0 }).count} queries
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: 'background.paper' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Department with Highest Usage
                </Typography>
                <Typography variant="h3" color="primary">
                  {usageByDepartment.reduce((max, dept) => dept.count > max.count ? dept : max, { count: 0 }).department}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  with {usageByDepartment.reduce((max, dept) => dept.count > max.count ? dept : max, { count: 0 }).count} queries
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      
      {/* User Analytics Tab */}
      {selectedTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
              <Typography variant="h6" gutterBottom>
                User Growth Over Time
              </Typography>
              <Box sx={{ height: 300 }}>
                <Line 
                  data={userGrowthData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
              <Typography variant="h6" gutterBottom>
                Top Users by Query Count
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar 
                  data={{
                    labels: topUsers.map(user => user.username),
                    datasets: [
                      {
                        label: 'Query Count',
                        data: topUsers.map(user => user.queryCount),
                        backgroundColor: 'rgba(54, 162, 235, 0.6)',
                        borderColor: 'rgba(54, 162, 235, 1)',
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
              <Typography variant="h6" gutterBottom>
                User Distribution by Department
              </Typography>
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                <Pie 
                  data={{
                    labels: [...new Set(topUsers.map(user => user.department))],
                    datasets: [
                      {
                        label: 'Users per Department',
                        data: [...new Set(topUsers.map(user => user.department))].map(
                          dept => topUsers.filter(user => user.department === dept).length
                        ),
                        backgroundColor: [
                          'rgba(255, 99, 132, 0.6)',
                          'rgba(54, 162, 235, 0.6)',
                          'rgba(255, 206, 86, 0.6)',
                          'rgba(75, 192, 192, 0.6)',
                        ],
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {/* Data Analytics Tab */}
      {selectedTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
              <Typography variant="h6" gutterBottom>
                Estimated Data Volume Over Time
              </Typography>
              <Box sx={{ height: 300 }}>
                <Line 
                  data={dataVolumeData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
              <Typography variant="h6" gutterBottom>
                Data Volume by AI Service
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar 
                  data={{
                    labels: usageByService.map(service => service.service),
                    datasets: [
                      {
                        label: 'Estimated Data Volume (KB)',
                        data: usageByService.map(service => service.count * 25), // Mocked data volume
                        backgroundColor: 'rgba(255, 99, 132, 0.6)',
                        borderColor: 'rgba(255, 99, 132, 1)',
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'y',
                    scales: {
                      x: {
                        beginAtZero: true,
                      }
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
              <Typography variant="h6" gutterBottom>
                Potential Data Exposure Risk
              </Typography>
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                <Pie 
                  data={{
                    labels: ['High Risk', 'Medium Risk', 'Low Risk'],
                    datasets: [
                      {
                        label: 'Risk Level',
                        data: [3, 4, 2], // Mocked risk data
                        backgroundColor: [
                          'rgba(255, 99, 132, 0.6)',
                          'rgba(255, 206, 86, 0.6)',
                          'rgba(75, 192, 192, 0.6)',
                        ],
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Analytics;